import { isConversationOperationConflict } from '../../../../utils/conversation-operation-conflict.util';
import { useRef, useState, useCallback, useMemo } from 'react';
import { useFunnelConversationDocuments } from '../../../../hooks/api/funnels/funnel-conversations';
import {
  FunnelConversationSearchExportBodyParams,
  SearcherDocumentSearchFilterOperator,
  CustomFieldEntityType,
  Exclude_CustomFieldEntityType_PROPERTY_,
} from 'src/api/generated';
import type {
  FunnelConversationDocument,
  SearcherDocumentSearchFilter,
  SearcherDocumentSearchSort,
} from 'src/api/generated';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import type { TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { PiChatCircle, PiDownloadSimple } from 'react-icons/pi';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import {
  useExportFunnelConversationTable,
  useUpdateFunnelConversation,
} from '../../../../hooks/api/funnels/funnel-conversations-mutations';
import { useBulkUpdateFunnelConversation } from '../../../../hooks/api/funnels/funnel-conversations-bulk-mutations';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import { BulkActions } from '../../../molecules/bulk-actions/bulk-actions';
import { BulkActionsMenu } from '../../../molecules/bulk-actions/bulk-actions-menu.tsx';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { Dialog } from 'src/modules/shared/components/molecules/dialog';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';

import { useFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import { createBasicColumns } from './column-renderers/basic-columns';
import {
  createConversationCustomFieldColumns,
  createContactCustomFieldColumns,
} from './column-renderers/custom-field-column';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { usePhoneContext } from 'src/contexts/phone-context';
import { useGlobalPhoneContext } from 'src/contexts/global-phone-context';
import STATES from 'states/index';
import { useAlert } from 'src/contexts/alert-context';
import { useUpdateContactAddressMutation } from 'src/modules/communications/hooks/api/contact-addresses.mutation';
import { useSpeedDialer } from 'src/modules/communications/context/speed-dialer.context';
import { SpeedDialControls } from '../../../molecules/speed-dial/speed-dial-controls';
import { SpeedDialControlsMenu } from '../../../molecules/speed-dial/speed-dial-controls-menu';
import type { SpeedDialerQueueItem } from 'src/modules/communications/types/speed-dialer.type';
import { queueFromConversations } from 'src/modules/communications/utils/speed-dialer.util';
import { isValidPhoneNumber } from 'src/modules/shared/utils/validation/phone.util';
import { InvalidPhoneDialog } from 'src/modules/communications/components/molecules/speed-dial/invalid-phone-dialog';
import { useSearch } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users.ts';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { Text } from 'src/modules/shared/components/atoms/text';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { PiDotsThreeVertical } from 'react-icons/pi';

const isExportFormat = (
  value: string
): value is FunnelConversationSearchExportBodyParams.format => {
  return (
    value === String(FunnelConversationSearchExportBodyParams.format.XLSX) ||
    value === String(FunnelConversationSearchExportBodyParams.format.CSV)
  );
};

const toConversationExportEntityType = (
  entityType: CustomFieldEntityType
): Exclude_CustomFieldEntityType_PROPERTY_ | null => {
  switch (entityType) {
    case CustomFieldEntityType.CONTACT:
      return Exclude_CustomFieldEntityType_PROPERTY_.CONTACT;
    case CustomFieldEntityType.COMPANY:
      return Exclude_CustomFieldEntityType_PROPERTY_.COMPANY;
    case CustomFieldEntityType.FUNNEL_CONVERSATION:
      return Exclude_CustomFieldEntityType_PROPERTY_.FUNNEL_CONVERSATION;
    case CustomFieldEntityType.FUNNEL_CHANNEL_SESSION:
      return Exclude_CustomFieldEntityType_PROPERTY_.FUNNEL_CHANNEL_SESSION;
    case CustomFieldEntityType.PRODUCT:
      return Exclude_CustomFieldEntityType_PROPERTY_.PRODUCT;
    case CustomFieldEntityType.PROPERTY:
      return null;
    default:
      return null;
  }
};

interface ConversationTableViewProps {
  /** Funnel id that scopes the table, when provided by the parent view. */
  selectedFunnelId?: number;
  /** Free-text search term currently applied to the table. */
  searchTerm?: string;
  /** Callback invoked when a row is selected from the table. */
  onConversationSelect?: (id: number) => void;
  /** Conversation id currently selected in the details panel. */
  selectedConversationId?: number;
  /** Active filters applied to the conversation search query. */
  filters: SearcherDocumentSearchFilter[];
  /** Active sort clauses applied to the conversation search query. */
  sorts?: SearcherDocumentSearchSort[];
}

/**
 * Renders the conversation table view with bulk actions, pagination, and export.
 */
export const ConversationTableView = ({
  searchTerm,
  onConversationSelect,
  selectedConversationId,
  filters,
  sorts = [],
}: ConversationTableViewProps) => {
  const centerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useI18n();

  const { conversationId, contactId } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      conversationId: search.conversationId,
      contactId: search.contactId,
    }),
  });

  const hasConversationPanel = !!conversationId;
  const hasContactPanel = !!contactId;
  const anyPanelOpen = hasConversationPanel || hasContactPanel;

  const inlineBreakpoint = anyPanelOpen ? 'hidden' : 'hidden 2xl:block';
  const compactBreakpoint = anyPanelOpen ? 'block' : 'block 2xl:hidden';

  const [selectedConversationIds, setSelectedConversationIds] = useState<
    string[]
  >([]);
  const [pageSize, setPageSize] = useState(20);
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<
    | { type: 'assign'; userId: number | null }
    | { type: 'archive' }
    | { type: 'move'; statusId: number }
    | null
  >(null);
  const [updatingAddressId, setUpdatingAddressId] = useState<number | null>(
    null
  );
  const [showExportFormatDialog, setShowExportFormatDialog] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<FunnelConversationSearchExportBodyParams.format>(
      FunnelConversationSearchExportBodyParams.format.XLSX
    );
  const { showAlert } = useAlert();

  const { users } = useAllPersonAndSystemUsers();
  const { provider: callProvider } = useGlobalCallProviderContext();
  const { loading: callProviderLoading = true, error: callProviderError } =
    callProvider ?? {};

  const { phoneState } = usePhoneContext();
  const { setGlobalPhoneVisible } = useGlobalPhoneContext();

  const isPhoneReady = phoneState === STATES.PHONE.READY;
  const isPhoneDisabled =
    phoneState === STATES.PHONE.ESTABLISHED ||
    phoneState === STATES.PHONE.OUTGOING ||
    phoneState === STATES.PHONE.INCOMING ||
    phoneState === STATES.PHONE.OFFLINE ||
    phoneState === STATES.PHONE.ERROR;

  const { funnelId: funnelIdFromSearch } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      funnelId: search.funnelId,
    }),
  });

  const { definitions: funnelOrderedDefinitions } =
    useFunnelCustomFieldDefinitions({
      funnelId: funnelIdFromSearch,
      enabled: !!funnelIdFromSearch,
    });

  const { updateAddress } = useUpdateContactAddressMutation(
    () => {
      setUpdatingAddressId(null);
    },
    (error) => {
      console.error('Failed to update phone address:', error);
      // TODO: check when implementing i18n
      showAlert(
        'Hubo un error al actualizar el teléfono. Por favor, inténtalo de nuevo.',
        'error'
      );
      setUpdatingAddressId(null);
    }
  );

  const statusQuery = useMemo(() => {
    if (!funnelIdFromSearch) return {};
    return createQuery()
      .equals('funnelId', funnelIdFromSearch)
      .orderByAsc('position')
      .build();
  }, [funnelIdFromSearch]);

  const { statuses } = useFunnelStatuses(statusQuery, !!funnelIdFromSearch);
  const { updateConversation, loading: updatingConversation } =
    useUpdateFunnelConversation();
  const { exportConversationTable, loading: exportingConversationTable } =
    useExportFunnelConversationTable(
      () => {
        showAlert(t('communications.table.export.alert.inProgress'), 'success');
      },
      (error) => {
        console.error('Failed to enqueue conversation table export:', error);
        showAlert(t('communications.table.export.alert.error'), 'error');
      }
    );

  const searchParams = useMemo(() => {
    if (!funnelIdFromSearch) return {};

    const baseFilters: SearcherDocumentSearchFilter[] = [
      {
        field: 'funnelId',
        operator: SearcherDocumentSearchFilterOperator.EQ,
        value: funnelIdFromSearch.toString(),
      },
    ];

    return {
      search: searchTerm,
      filters: [...baseFilters, ...filters],
      ...(sorts.length > 0 && { sort: sorts }),
      includeTotalCount: true,
      size: pageSize,
    };
  }, [funnelIdFromSearch, searchTerm, filters, sorts, pageSize]);

  const {
    conversations,
    loading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
  } = useFunnelConversationDocuments(searchParams, !!funnelIdFromSearch);

  const {
    queue,
    currentConversationId,
    currentPhoneNumber,
    isSessionActive,
    startSession,
    stopSession,
    currentPhoneIndex,
    updatePhoneNumber,
    skipCall,
  } = useSpeedDialer();

  const onBulkUpdateSuccess = useCallback(() => {
    setSelectedConversationIds([]);
    setShowBulkConfirmation(false);
    setPendingBulkAction(null);
  }, []);
  const { bulkUpdateConversations, loading: updatingBulkConversations } =
    useBulkUpdateFunnelConversation(onBulkUpdateSuccess);

  const handleStatusChange = useCallback(
    async (conversationId: number, statusId: number) => {
      await updateConversation({
        id: conversationId,
        data: { funnelStatusId: statusId },
      });
    },
    [updateConversation]
  );

  const handleUserAssign = useCallback(
    async (conversationId: number, userId: number | null) => {
      await updateConversation({
        id: conversationId,
        data: { userId },
      });
    },
    [updateConversation]
  );

  const handleArchiveToggle = useCallback(
    async (conversationId: number) => {
      const conversation = conversations.find(
        (conv) => Number(conv.id) === conversationId
      );
      if (!conversation) return;

      const isArchived = !!conversation.archivedAt;

      try {
        await updateConversation({
          id: conversationId,
          data: { archivedAt: isArchived ? null : new Date().toISOString() },
        });

        // TODO: check when implementing i18n
        showAlert(
          isArchived
            ? 'La conversación ha sido restaurada'
            : 'La conversación ha sido archivada',
          'success'
        );
      } catch (error) {
        if (isConversationOperationConflict(error)) return;
        console.error('Failed to toggle archive status:', error);
        // TODO: check when implementing i18n
        showAlert(
          'Hubo un error al actualizar la conversación. Por favor, inténtalo de nuevo.',
          'error'
        );
      }
    },
    [conversations, updateConversation, showAlert]
  );

  const handleCall = useCallback(
    (phoneNumber: string) => {
      if (callProviderLoading || !callProvider || !isPhoneReady) return;

      setGlobalPhoneVisible(true);
      void callProvider.callActions.startCall({ phoneNumber });
    },
    [callProvider, callProviderLoading, isPhoneReady, setGlobalPhoneVisible]
  );

  const handleUpdateAddress = useCallback(
    async (addressId: number, data: { address: string }) => {
      setUpdatingAddressId(addressId);
      await updateAddress({ id: addressId, data });
    },
    [updateAddress]
  );

  const handleBulkUserAssign = useCallback((userId: number | null) => {
    setPendingBulkAction({ type: 'assign', userId });
    setShowBulkConfirmation(true);
  }, []);

  const handleBulkArchive = useCallback(() => {
    setPendingBulkAction({ type: 'archive' });
    setShowBulkConfirmation(true);
  }, []);

  const handleBulkMoveStatus = useCallback((statusId: number) => {
    setPendingBulkAction({ type: 'move', statusId });
    setShowBulkConfirmation(true);
  }, []);

  const handleExportConversationTable = useCallback(
    async (
      format: FunnelConversationSearchExportBodyParams.format = FunnelConversationSearchExportBodyParams
        .format.XLSX
    ) => {
      if (!funnelIdFromSearch) return;

      const customColumns = funnelOrderedDefinitions.flatMap((field) => {
        const exportEntityType = toConversationExportEntityType(
          field.entityType
        );

        return exportEntityType
          ? [
              {
                fieldKey: field.fieldKey,
                fieldLabel: field.fieldLabel ?? undefined,
                fieldType: field.fieldType,
                entityType: exportEntityType,
                includeTime: resolveDateCustomFieldIncludeTime(
                  field.fieldType,
                  field.includeTime
                ),
              },
            ]
          : [];
      });
      const exportFilters: SearcherDocumentSearchFilter[] = [
        {
          field: 'funnelId',
          operator: SearcherDocumentSearchFilterOperator.EQ,
          value: funnelIdFromSearch.toString(),
        },
        ...filters,
      ];

      const exportRequest = {
        search: searchTerm,
        filters: exportFilters,
        sort: sorts.length > 0 ? sorts : undefined,
        funnelId: funnelIdFromSearch,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        customColumns,
        format,
      };

      await exportConversationTable(exportRequest);
    },
    [
      funnelIdFromSearch,
      funnelOrderedDefinitions,
      exportConversationTable,
      searchTerm,
      filters,
      sorts,
    ]
  );

  const handleOpenExportDialog = useCallback(() => {
    if (!funnelIdFromSearch) {
      return;
    }

    setShowExportFormatDialog(true);
  }, [funnelIdFromSearch]);

  const handleConfirmExportFormat = useCallback(async () => {
    await handleExportConversationTable(exportFormat);
    setShowExportFormatDialog(false);
  }, [handleExportConversationTable, exportFormat]);

  const paginationMenuItems = useMemo<DropdownItem[]>(
    () => [
      {
        onClick: handleOpenExportDialog,
        disabled: exportingConversationTable || !funnelIdFromSearch,
        icon: <PiDownloadSimple className="w-4 h-4 text-secondary-700" />,
        content: t('communications.table.export.button'),
      },
    ],
    [exportingConversationTable, funnelIdFromSearch, handleOpenExportDialog, t]
  );

  const handleConfirmBulkAction = useCallback(async () => {
    if (!pendingBulkAction) return;

    if (pendingBulkAction.type === 'assign') {
      await bulkUpdateConversations({
        conversationIds: selectedConversationIds.map(Number),
        data: { userId: pendingBulkAction.userId },
      });
      return;
    }

    if (pendingBulkAction.type === 'archive') {
      await bulkUpdateConversations({
        conversationIds: selectedConversationIds.map(Number),
        data: { archivedAt: new Date().toISOString() },
      });
    }

    if (pendingBulkAction.type === 'move') {
      await bulkUpdateConversations({
        conversationIds: selectedConversationIds.map(Number),
        data: { funnelStatusId: pendingBulkAction.statusId },
      });
    }
  }, [pendingBulkAction, selectedConversationIds, bulkUpdateConversations]);

  const handleStartPowerDialing = useCallback(() => {
    const selectedConversations = conversations.filter((conv) =>
      selectedConversationIds.includes(conv.id)
    );

    const queueItems: SpeedDialerQueueItem[] = queueFromConversations(
      selectedConversations
    );

    if (queueItems.length === 0) {
      // TODO: check when implementing i18n
      showAlert(
        'No hay números de teléfono válidos para las conversaciones seleccionadas.',
        'warning'
      );
      return;
    }

    startSession(queueItems);
  }, [conversations, selectedConversationIds, startSession, showAlert]);

  const columns = useMemo<
    TableColumnProps<FunnelConversationDocument>[]
  >(() => {
    const consolidatedPhoneState = {
      isLoading: callProviderLoading,
      hasError: !!callProviderError,
      isReady: isPhoneReady,
      isDisabled: isPhoneDisabled,
    };

    const baseColumns = createBasicColumns({
      users: users.map((user) => ({
        id: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
      })),
      statuses,
      testAddressReadOnlyTooltip: t(
        'communications.testConversations.contactAddress.readOnlyTooltip'
      ),
      selectedConversationId,
      onConversationSelect,
      handleStatusChange: (conversationId, statusId) => {
        void handleStatusChange(conversationId, statusId);
      },
      handleUserAssign: (conversationId, userId) => {
        void handleUserAssign(conversationId, userId);
      },
      handleCall,
      handleArchive: handleArchiveToggle,
      phoneState: consolidatedPhoneState,
      updatingConversation,
      variant: 'neutral',
      handleUpdateAddress,
      updatingAddressId: updatingAddressId ?? undefined,
      powerDialingActive: isSessionActive,
      currentPowerDialingId: currentConversationId,
      currentPhoneNumber,
    });

    let customColumns: TableColumnProps<FunnelConversationDocument>[] = [];
    if (funnelIdFromSearch && funnelOrderedDefinitions.length > 0) {
      customColumns = funnelOrderedDefinitions.flatMap((field) => {
        const common = [
          {
            fieldKey: field.fieldKey,
            fieldLabel: field.fieldLabel ?? undefined,
            fieldType: field.fieldType,
          },
        ];
        if (field.entityType === CustomFieldEntityType.FUNNEL_CONVERSATION) {
          return createConversationCustomFieldColumns({
            customFields: common,
            variant: 'neutral',
          });
        }
        if (field.entityType === CustomFieldEntityType.CONTACT) {
          return createContactCustomFieldColumns({
            customFields: common,
            variant: 'neutral',
          });
        }
        return [];
      });
    }

    return [
      ...baseColumns.slice(0, baseColumns.length - 1),

      ...customColumns,

      baseColumns[baseColumns.length - 1],
    ];
  }, [
    callProviderLoading,
    callProviderError,
    isPhoneReady,
    isPhoneDisabled,
    users,
    statuses,
    selectedConversationId,
    onConversationSelect,
    handleStatusChange,
    handleUserAssign,
    handleCall,
    handleArchiveToggle,
    updatingConversation,
    funnelOrderedDefinitions,
    funnelIdFromSearch,
    handleUpdateAddress,
    updatingAddressId,
    isSessionActive,
    currentConversationId,
    currentPhoneNumber,
    t,
  ]);

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedConversationIds(selectedIds);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
  };

  const isCurrentPhoneValid = isValidPhoneNumber(currentPhoneNumber);

  const handleSaveInvalidPhone = async (newPhone: string) => {
    if (!currentConversationId) return;
    const currentDialerItem = queue.find(
      (item) => item.conversationId === currentConversationId
    );
    if (
      !currentDialerItem?.phoneAddressIds ||
      currentDialerItem.phoneAddressIds.length <= currentPhoneIndex
    ) {
      return;
    }

    const addressId = currentDialerItem.phoneAddressIds[currentPhoneIndex];
    await handleUpdateAddress(addressId, { address: newPhone });
    updatePhoneNumber(newPhone);
  };

  const showInvalidPhoneDialog =
    isSessionActive && !!currentPhoneNumber && !isCurrentPhoneValid;

  if (error) {
    return (
      // TODO: check when implementing i18n
      <ErrorState
        // TODO: check when implementing i18n
        title="Error al cargar conversaciones"
        description={error.message}
        onAction={() => window.location.reload()}
      />
    );
  }

  const generateRowClassName = (row: FunnelConversationDocument) => {
    const classes = ['group'];
    if (isSessionActive && row.id === currentConversationId) {
      classes.push('animate-row-calling relative');
    }
    if (row.archivedAt) {
      classes.push('opacity-80 hover:opacity-100 bg-neutral-100');
    }
    return classes.join(' ');
  };

  return (
    // TODO: fix and collapse on smaller widths (currently overflows)
    <div className="flex flex-col h-full p-4 gap-1">
      <TokenPagination
        pageSize={pageSize}
        totalCount={totalCount}
        currentCount={conversations.length}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isLoading={isFetchingNextPage}
        density={anyPanelOpen ? 'compact' : 'default'}
        endAddon={
          <MenuDropdown
            items={paginationMenuItems}
            handlerComponent={
              <Button
                size="md"
                variant="neutral"
                appearance="ghost"
                isIconOnly
                aria-label={t('communications.conversationHeader.moreActions')}
              >
                <PiDotsThreeVertical className="h-5 w-5" />
              </Button>
            }
            placement="bottom-end"
            size="sm"
          />
        }
        centerContent={
          <div
            ref={centerRef}
            className="flex items-center gap-4 relative min-w-0"
          >
            {isSessionActive ? (
              <>
                <div className={inlineBreakpoint}>
                  <SpeedDialControls />
                </div>

                <div className={compactBreakpoint}>
                  <SpeedDialControlsMenu />
                </div>
              </>
            ) : (
              <>
                <div className={inlineBreakpoint}>
                  <BulkActions
                    selectedCount={selectedConversationIds.length}
                    onUserAssign={handleBulkUserAssign}
                    statuses={statuses}
                    onMoveStatus={handleBulkMoveStatus}
                    isLoading={updatingBulkConversations}
                    onPowerDial={handleStartPowerDialing}
                    powerDialingActive={isSessionActive}
                    onArchive={handleBulkArchive}
                  />
                </div>

                <div className={compactBreakpoint}>
                  <BulkActionsMenu
                    selectedCount={selectedConversationIds.length}
                    onUserAssign={handleBulkUserAssign}
                    statuses={statuses}
                    onMoveStatus={handleBulkMoveStatus}
                    isLoading={updatingBulkConversations}
                    onPowerDial={handleStartPowerDialing}
                    powerDialingActive={isSessionActive}
                    onArchive={handleBulkArchive}
                  />
                </div>
              </>
            )}
          </div>
        }
      />

      <Panel className="h-full min-h-0 pt-0">
        <Table<FunnelConversationDocument>
          columns={columns}
          data={conversations}
          keyExtractor={(item: FunnelConversationDocument) => item.id}
          selectedIds={selectedConversationIds}
          onSelectionChange={handleSelectionChange}
          variant="neutral"
          isLoading={loading}
          emptyStateIcon={
            <PiChatCircle className="text-primary-500 w-full h-full" />
          }
          // TODO: check when implementing i18n
          emptyStateMessage="No hay conversaciones"
          emptyStateDescription="Inicia una nueva conversación o cambia tus filtros"
          headerClassName="min-w-[200px]"
          fixedFirstColumn
          rowClassName={generateRowClassName}
        />
      </Panel>

      <Dialog
        isOpen={showExportFormatDialog}
        onClose={() => setShowExportFormatDialog(false)}
        title={t('communications.table.export.dialog.title')}
        description={
          <div className="space-y-3">
            <Text size="tiny" className="text-secondary-600">
              {t('communications.table.export.dialog.description')}
            </Text>
            <Select
              value={exportFormat}
              onChange={(event) => {
                const { value } = event.target;
                if (!isExportFormat(value)) {
                  return;
                }

                setExportFormat(value);
              }}
              options={[
                {
                  value: FunnelConversationSearchExportBodyParams.format.XLSX,
                  content: t('communications.table.export.format.xlsx'),
                },
                {
                  value: FunnelConversationSearchExportBodyParams.format.CSV,
                  content: t('communications.table.export.format.csv'),
                },
              ]}
              variant="neutral"
            />
          </div>
        }
        confirmLabel={t('communications.table.export.dialog.confirm')}
        cancelLabel={t('communications.table.export.dialog.cancel')}
        onConfirm={handleConfirmExportFormat}
        isLoading={exportingConversationTable}
        variant="neutral"
      />

      <Dialog
        isOpen={showBulkConfirmation}
        onClose={() => {
          setShowBulkConfirmation(false);
          setPendingBulkAction(null);
        }}
        // TODO: check when implementing i18n
        title="Confirmar"
        // TODO: check when implementing i18n
        description="Esta acción actualizará múltiples conversaciones en segundo plano. Los cambios pueden tardar unos momentos en reflejarse en el sistema. ¿Quieres proceder?"
        // TODO: check when implementing i18n
        confirmLabel="Proceder"
        // TODO: check when implementing i18n
        cancelLabel="Cancelar"
        onConfirm={handleConfirmBulkAction}
        isLoading={updatingBulkConversations}
      />

      <InvalidPhoneDialog
        isOpen={showInvalidPhoneDialog}
        phoneNumber={currentPhoneNumber ?? ''}
        onSave={handleSaveInvalidPhone}
        onSkip={skipCall}
        onStopSession={stopSession}
      />
    </div>
  );
};
